import {
	createAdminApiClient,
	type AdminApiClient,
} from "@shopify/admin-api-client";
import {
	createStorefrontApiClient,
	type StorefrontApiClient,
} from "@shopify/storefront-api-client";
import {log} from "./log";
import {SHOPIFY_API_VERSION} from "./version";

// Number of retry attempts. Shopify's clients handle the retry loop
// themselves; 1 means up to one retry (two attempts total).
const RETRIES = 1;

/**
 * Credentials for the Shopify Admin / Storefront clients.
 *
 * Prefer {@link configureShopify} in runtimes where env is not populated at
 * process start (connector Lambdas via `loadSecrets`). Next.js / Vercel can
 * skip configure and rely on `process.env` — clients are still built lazily
 * on first use and then cached as module-level singletons.
 */
export type ShopifyConfig = {
	storeDomain: string;
	adminToken: string;
	/** Required only when a Storefront API call is made. */
	storefrontToken?: string;
};

let configured: ShopifyConfig | undefined;
let storefrontClient: StorefrontApiClient | undefined;
let adminClient: AdminApiClient | undefined;

/**
 * Supply credentials explicitly and drop any cached clients so the next
 * request rebuilds them. Connector outbound cold starts call this with values
 * from `loadSecrets()` so secrets never need to be written into `process.env`.
 */
export function configureShopify(config: ShopifyConfig): void {
	configured = config;
	storefrontClient = undefined;
	adminClient = undefined;
}

function resolveCredential(
	api: ShopifyApi,
	fromConfig: string | undefined,
	envKey: string
): string {
	// Empty string falls through so a partial configureShopify() can still
	// pick remaining fields from process.env (Next.js / local).
	const value = fromConfig || process.env[envKey];
	if (!value) {
		throw new Error(
			`Shopify ${api} client is missing ${envKey}. Call configureShopify() ` +
				`with the credential, or set ${envKey} in the environment.`
		);
	}
	return value;
}

function getStorefrontClient(): StorefrontApiClient {
	if (!storefrontClient) {
		storefrontClient = createStorefrontApiClient({
			storeDomain: resolveCredential(
				"storefront",
				configured?.storeDomain,
				"SHOPIFY_STORE_DOMAIN"
			),
			apiVersion: SHOPIFY_API_VERSION,
			publicAccessToken: resolveCredential(
				"storefront",
				configured?.storefrontToken,
				"SHOPIFY_STOREFRONT_TOKEN"
			),
			retries: RETRIES,
		});
	}
	return storefrontClient;
}

function getAdminClient(): AdminApiClient {
	if (!adminClient) {
		adminClient = createAdminApiClient({
			storeDomain: resolveCredential(
				"admin",
				configured?.storeDomain,
				"SHOPIFY_STORE_DOMAIN"
			),
			apiVersion: SHOPIFY_API_VERSION,
			accessToken: resolveCredential(
				"admin",
				configured?.adminToken,
				"SHOPIFY_ADMIN_TOKEN"
			),
			retries: RETRIES,
		});
	}
	return adminClient;
}

const MODULE = "@repo/shopify";
const FN = "makeShopifyRequest";

export type ShopifyApi = "storefront" | "admin";

/** Escapes a value for interpolation into a Shopify search query string. */
export function escapeSearchQueryValue(value: string): string {
	return value.replace(/\\/g, "\\\\").replace(/"/g, '\\"');
}

/**
 * This function is meant to be used by functions that wrap the actual GraphQL
 * queries that are sent to Shopify. For TData, pass in a codegen'd type that is
 * generated from the query in the calling function. That way, the calling
 * function will know the type of the object returned by the query.
 */
export async function makeShopifyRequest<TData>(
	api: ShopifyApi,
	query: string,
	variables: Record<string, unknown>
): Promise<TData> {
	const client =
		api === "storefront" ? getStorefrontClient() : getAdminClient();
	const response = await client.request<TData>(query, {variables});

	if (response.errors) {
		const {networkStatusCode, message, graphQLErrors} = response.errors;
		const label = graphQLErrors?.length ? "E_GRAPHQL_ERROR" : "E_HTTP_ERROR";
		log.error(
			{
				module: MODULE,
				fn: FN,
				label,
				api,
				apiVersion: SHOPIFY_API_VERSION,
				status: networkStatusCode,
				graphqlErrors: graphQLErrors,
				errorMessage: message,
			},
			`Shopify ${api} request failed`
		);
		throw new Error(
			`Shopify ${api} request failed: ${message ?? "unknown error"}`
		);
	}

	if (!response.data) {
		log.error(
			{
				module: MODULE,
				fn: FN,
				label: "E_EMPTY_RESPONSE",
				api,
				apiVersion: SHOPIFY_API_VERSION,
			},
			`Shopify ${api} response missing data`
		);
		throw new Error(`Shopify ${api} response missing data`);
	}

	return response.data;
}
